// src/encounter/care-plan-tab/use-care-plan-teams.ts
// Each plan's care team for the board's team strip: A2 GET .../care-plans/{carePlanId}/team (fetchCareTeam), one query
// per plan on the same key as useCareTeam (so the L360 panel and this tab share the cache). The rows are joined to the
// plan's activities (lanes, "x/y signed") by care-plan-board.ts teamStrip. A plan whose server records no contributors
// (contributorsAvailable false) and lists no task rows says so instead of reading as "no team".
import { useQueries } from '@tanstack/react-query';
import { fetchCareTeam } from '../services/l360-api';
import { careTeamKey } from '../services/use-l360-activation';
import type { CarePlanView } from '../lib/care-plan-view';
import { teamStrip, type CarePlanViewer } from './care-plan-board';
import type { CarePlanTeamState } from './care-plan-team';

export function useCarePlanTeams(
  patientId: string,
  plans: readonly CarePlanView[],
  viewer: CarePlanViewer | null,
): Record<string, CarePlanTeamState> {
  const reads = useQueries({
    queries: plans.map((p) => ({
      queryKey: careTeamKey(patientId, p.carePlanId),
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchCareTeam(patientId, p.carePlanId, signal),
    })),
  });
  return Object.fromEntries(
    plans.map((p, i): [string, CarePlanTeamState] => {
      const read = reads[i];
      if (read.isError) return [p.carePlanId, { kind: 'error' }];
      if (!read.data) return [p.carePlanId, { kind: 'loading' }];
      const members = teamStrip(read.data.members ?? [], p.activities ?? [], viewer);
      // A2 still lists task rows when contributors are not recorded; with none at all, say "not recorded", not "no team".
      if (members.length === 0 && p.contributorsAvailable === false) return [p.carePlanId, { kind: 'unavailable' }];
      return [p.carePlanId, { kind: 'members', members }];
    }),
  );
}
